import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Download,
  Type,
  Sun,
  Contrast,
  Palette,
  RotateCcw,
  Plus,
  Trash2,
  Loader2,
  Wand2,
  ScanText,
  Replace,
  Undo2,
  Save
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_HISTORY = 10;

const FONTS = [
  { value: "Impact", label: "Impact (Classic)" },
  { value: "Arial Black", label: "Arial Black" },
  { value: "Bebas Neue", label: "Bebas Neue" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Figtree", label: "Figtree" },
  { value: "Glacial Indifference", label: "Glacial Indifference" },
  { value: "Poppins", label: "Poppins" },
  { value: "Inter", label: "Inter" },
  { value: "Oswald", label: "Oswald" },
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
];

const TEXT_COLORS = [
  { value: "#FFFFFF", label: "Weiß" },
  { value: "#FFFF00", label: "Gelb" },
  { value: "#FF0000", label: "Rot" },
  { value: "#00FF00", label: "Grün" },
  { value: "#00FFFF", label: "Cyan" },
  { value: "#FF00FF", label: "Magenta" },
  { value: "#000000", label: "Schwarz" },
];

const MetaAdEditor = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobId = searchParams.get("jobId");
  const imageIndex = parseInt(searchParams.get("index") || "0");
  
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [originalImage, setOriginalImage] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [currentFormat, setCurrentFormat] = useState("1:1");
  
  // Image adjustments
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  
  // Text overlays
  const [textOverlays, setTextOverlays] = useState([]);
  const [activeTextIndex, setActiveTextIndex] = useState(null);
  const [newText, setNewText] = useState("");
  const [newTextFont, setNewTextFont] = useState("Impact");
  const [newTextColor, setNewTextColor] = useState("#FFFFFF");
  const [newTextSize, setNewTextSize] = useState(48);
  const [newTextStroke, setNewTextStroke] = useState(true);
  const [newTextLetterSpacing, setNewTextLetterSpacing] = useState(0);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // AI Text Editing
  const [detectedTexts, setDetectedTexts] = useState([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [selectedDetectedText, setSelectedDetectedText] = useState(null);
  const [replacementText, setReplacementText] = useState("");
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Undo History
  const [imageHistory, setImageHistory] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get canvas dimensions based on format
  const getCanvasDimensions = useCallback(() => {
    if (currentFormat === "9:16") {
      return { width: 1080, height: 1920 };
    } else if (currentFormat === "1200x628") {
      return { width: 1200, height: 628 };
    }
    // 1:1
    return { width: 1080, height: 1080 };
  }, [currentFormat]);

  // Load job and image data
  useEffect(() => {
    const loadJobData = async () => {
      if (!jobId) {
        toast.error("Keine Job-ID angegeben");
        navigate(-1);
        return;
      }
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/jobs/${jobId}`);
        const job = response.data;
        setJobData(job);
        
        // Get images - try result_ids first (new format), then inline data (legacy)
        let allImages = [];
        
        // New format: images stored separately via result_ids
        if (job.result_ids?.length > 0) {
          const thumbnailPromises = job.result_ids.map(id => 
            axios.get(`${BACKEND_URL}/api/thumbnails/${id}`).then(res => res.data).catch(() => null)
          );
          const thumbnails = (await Promise.all(thumbnailPromises)).filter(t => t !== null);
          allImages = thumbnails.map(t => ({
            image_data: t.image_data,
            format: t.meta_ad_format || t.format || "1:1",
            variation: t.variation_number || t.variation,
            id: t.id
          }));
        }
        
        // Fallback: inline images in result_data
        if (allImages.length === 0) {
          const directImages = job.result_data?.images || [];
          const formats = job.result_data?.formats || {};
          
          if (directImages.length > 0) {
            directImages.forEach((imgData, idx) => {
              if (typeof imgData === 'object') {
                allImages.push({
                  image_data: imgData.image || imgData.image_data,
                  format: imgData.format || "1:1",
                  export_format: imgData.export_format,
                  variation: imgData.variation,
                  width: imgData.width,
                  height: imgData.height,
                  id: `meta-ad-${idx}`
                });
              } else {
                allImages.push({ image_data: imgData, format: "1:1", id: `meta-ad-${idx}` });
              }
            });
          }
          
          Object.entries(formats).forEach(([formatType, images]) => {
            images.forEach((img) => {
              allImages.push({ ...img, format: formatType });
            });
          });
        }
        
        if (allImages.length > imageIndex) {
          const selectedImage = allImages[imageIndex];
          setImageData(selectedImage);
          setCurrentFormat(selectedImage.format || "1:1");
          
          // Load image
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            setOriginalImage(img);
            setIsLoading(false);
          };
          img.onerror = () => {
            toast.error("Fehler beim Laden des Bildes");
            setIsLoading(false);
          };
          img.src = `data:image/png;base64,${selectedImage.image_data}`;
        } else {
          toast.error("Bild nicht gefunden");
          navigate(-1);
        }
      } catch (err) {
        console.error("Error loading job:", err);
        toast.error("Fehler beim Laden des Auftrags");
        navigate(-1);
      }
    };
    
    loadJobData();
  }, [jobId, imageIndex, navigate]);

  // Render canvas
  const renderCanvas = useCallback(() => {
    if (!canvasRef.current || !originalImage) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { width: targetWidth, height: targetHeight } = getCanvasDimensions();
    
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Apply filters
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    
    // Draw image scaled to fit
    const scale = Math.min(
      targetWidth / originalImage.width,
      targetHeight / originalImage.height
    );
    const x = (targetWidth - originalImage.width * scale) / 2;
    const y = (targetHeight - originalImage.height * scale) / 2;
    ctx.drawImage(originalImage, x, y, originalImage.width * scale, originalImage.height * scale);
    
    // Reset filter for text
    ctx.filter = "none";
    
    // Draw text overlays
    textOverlays.forEach((overlay, index) => {
      ctx.font = `bold ${overlay.size}px ${overlay.font}`;
      ctx.fillStyle = overlay.color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.letterSpacing = `${overlay.letterSpacing || 0}px`;
      
      // Draw stroke if enabled
      if (overlay.stroke) {
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = overlay.size / 10;
        ctx.lineJoin = "round";
        ctx.strokeText(overlay.text, overlay.x, overlay.y);
      }
      
      // Draw text
      ctx.fillText(overlay.text, overlay.x, overlay.y);
      
      // Reset letterSpacing
      ctx.letterSpacing = "0px";
      
      // Draw selection box if active
      if (index === activeTextIndex) {
        const metrics = ctx.measureText(overlay.text);
        const textWidth = metrics.width;
        const textHeight = overlay.size;
        
        ctx.strokeStyle = "#0EA5A4";
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(
          overlay.x - textWidth / 2 - 10,
          overlay.y - textHeight / 2 - 5,
          textWidth + 20,
          textHeight + 10
        );
        ctx.setLineDash([]);
      }
    });
  }, [originalImage, brightness, contrast, saturation, textOverlays, activeTextIndex, getCanvasDimensions]);

  // Re-render when dependencies change
  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  // Add text overlay
  const addTextOverlay = () => {
    if (!newText.trim()) {
      toast.error("Bitte Text eingeben");
      return;
    }
    
    const { width, height } = getCanvasDimensions();
    const newOverlay = {
      text: newText,
      font: newTextFont,
      color: newTextColor,
      size: newTextSize,
      stroke: newTextStroke,
      letterSpacing: newTextLetterSpacing,
      x: width / 2,
      y: height / 2,
    };
    
    setTextOverlays([...textOverlays, newOverlay]);
    setActiveTextIndex(textOverlays.length);
    setNewText("");
    toast.success("Text hinzugefügt");
  };

  // Remove text overlay
  const removeTextOverlay = (index) => {
    setTextOverlays(textOverlays.filter((_, i) => i !== index));
    setActiveTextIndex(null);
    toast.success("Text entfernt");
  };

  // Handle canvas mouse events for dragging
  const handleCanvasMouseDown = (e) => {
    if (activeTextIndex === null) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    const overlay = textOverlays[activeTextIndex];
    if (overlay) {
      const ctx = canvas.getContext("2d");
      ctx.font = `bold ${overlay.size}px ${overlay.font}`;
      const metrics = ctx.measureText(overlay.text);
      const textWidth = metrics.width;
      const textHeight = overlay.size;
      
      if (
        x >= overlay.x - textWidth / 2 - 10 &&
        x <= overlay.x + textWidth / 2 + 10 &&
        y >= overlay.y - textHeight / 2 - 5 &&
        y <= overlay.y + textHeight / 2 + 5
      ) {
        setIsDragging(true);
        setDragOffset({ x: x - overlay.x, y: y - overlay.y });
      }
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!isDragging || activeTextIndex === null) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    const { width, height } = getCanvasDimensions();
    
    setTextOverlays(textOverlays.map((overlay, index) => {
      if (index === activeTextIndex) {
        return {
          ...overlay,
          x: Math.max(50, Math.min(width - 50, x - dragOffset.x)),
          y: Math.max(50, Math.min(height - 50, y - dragOffset.y)),
        };
      }
      return overlay;
    }));
  };

  const handleCanvasMouseUp = () => {
    setIsDragging(false);
  };

  // Click on canvas to select text
  const handleCanvasClick = (e) => {
    if (isDragging) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    let foundIndex = null;
    const ctx = canvas.getContext("2d");
    
    textOverlays.forEach((overlay, index) => {
      ctx.font = `bold ${overlay.size}px ${overlay.font}`;
      const metrics = ctx.measureText(overlay.text);
      const textWidth = metrics.width;
      const textHeight = overlay.size;
      
      if (
        x >= overlay.x - textWidth / 2 - 10 &&
        x <= overlay.x + textWidth / 2 + 10 &&
        y >= overlay.y - textHeight / 2 - 5 &&
        y <= overlay.y + textHeight / 2 + 5
      ) {
        foundIndex = index;
      }
    });
    
    setActiveTextIndex(foundIndex);
  };

  // Reset adjustments
  const resetAdjustments = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    toast.success("Anpassungen zurückgesetzt");
  };

  // Download
  const downloadImage = (format = "png") => {
    if (!canvasRef.current) return;
    
    const prevActive = activeTextIndex;
    setActiveTextIndex(null);
    
    setTimeout(() => {
      const canvas = canvasRef.current;
      const mimeType = format === "jpg" ? "image/jpeg" : "image/png";
      const dataUrl = canvas.toDataURL(mimeType, 0.95);
      
      const link = document.createElement("a");
      link.download = `meta-ad-${jobData?.title || 'edited'}.${format}`;
      link.href = dataUrl;
      link.click();
      
      setActiveTextIndex(prevActive);
      toast.success(`Meta Ad als ${format.toUpperCase()} heruntergeladen`);
    }, 100);
  };

  // AI Text Detection
  const detectTextInImage = async () => {
    if (!originalImage) return;
    
    setIsDetecting(true);
    try {
      // Send original image at FULL resolution
      const fullResCanvas = document.createElement("canvas");
      fullResCanvas.width = originalImage.width;
      fullResCanvas.height = originalImage.height;
      const fullResCtx = fullResCanvas.getContext("2d");
      fullResCtx.drawImage(originalImage, 0, 0);
      const imageDataBase64 = fullResCanvas.toDataURL("image/png").split(",")[1];
      
      const response = await axios.post(`${BACKEND_URL}/api/editor/detect-text`, {
        image_data: imageDataBase64
      });
      
      if (response.data.texts && response.data.texts.length > 0) {
        setDetectedTexts(response.data.texts);
        toast.success(`${response.data.texts.length} Text(e) erkannt`);
      } else {
        setDetectedTexts([]);
        toast.info("Kein Text im Bild erkannt");
      }
    } catch (err) {
      console.error("Text detection error:", err);
      toast.error("Fehler bei der Texterkennung");
    } finally {
      setIsDetecting(false);
    }
  };

  // Save current image to database at full resolution (manual action)
  const saveImageToDb = async () => {
    if (!imageData?.id || !canvasRef.current) return;
    setIsSaving(true);
    try {
      const canvas = canvasRef.current;
      const { width, height } = getCanvasDimensions();
      // Ensure full-res export at native ad dimensions
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = width;
      exportCanvas.height = height;
      const ctx = exportCanvas.getContext("2d");
      ctx.drawImage(canvas, 0, 0, width, height);
      const base64Data = exportCanvas.toDataURL("image/png").split(",")[1];
      await axios.put(`${BACKEND_URL}/api/thumbnails/${imageData.id}/update-image`, {
        image_data: base64Data
      });
      setHasUnsavedChanges(false);
      toast.success("Bild gespeichert!");
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  // AI Text Replacement
  const replaceTextInImage = async () => {
    if (!selectedDetectedText || !replacementText.trim()) {
      toast.error("Bitte Text auswählen und neuen Text eingeben");
      return;
    }
    
    setIsReplacing(true);
    try {
      // Send original image at FULL resolution
      const fullResCanvas = document.createElement("canvas");
      fullResCanvas.width = originalImage.width;
      fullResCanvas.height = originalImage.height;
      const fullResCtx = fullResCanvas.getContext("2d");
      fullResCtx.drawImage(originalImage, 0, 0);
      const imageDataBase64 = fullResCanvas.toDataURL("image/png").split(",")[1];
      
      if (originalImage) {
        setImageHistory(prev => {
          const newHistory = [...prev, originalImage];
          if (newHistory.length > MAX_HISTORY) {
            return newHistory.slice(-MAX_HISTORY);
          }
          return newHistory;
        });
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/editor/replace-text`, {
        image_data: imageDataBase64,
        original_text: selectedDetectedText.text,
        new_text: replacementText
      });
      
      if (response.data.success && response.data.image_data) {
        const editedImg = new Image();
        editedImg.crossOrigin = "anonymous";
        editedImg.onload = () => {
          setOriginalImage(editedImg);
          setShowReplaceDialog(false);
          setSelectedDetectedText(null);
          setReplacementText("");
          setDetectedTexts([]);
          setHasUnsavedChanges(true);
          toast.success(`Text "${selectedDetectedText.text}" wurde ersetzt!`);
        };
        editedImg.onerror = () => {
          setImageHistory(prev => prev.slice(0, -1));
          toast.error("Fehler beim Laden des bearbeiteten Bildes");
        };
        editedImg.src = `data:image/png;base64,${response.data.image_data}`;
      } else {
        setImageHistory(prev => prev.slice(0, -1));
        toast.error(response.data.error || "KI konnte den Text nicht ersetzen");
      }
    } catch (err) {
      console.error("Text replacement error:", err);
      setImageHistory(prev => prev.slice(0, -1));
      toast.error("Fehler bei der KI-Textbearbeitung");
    } finally {
      setIsReplacing(false);
    }
  };

  // AI Text Deletion
  const deleteTextInImage = async () => {
    if (!selectedDetectedText) {
      toast.error("Bitte Text zum Löschen auswählen");
      return;
    }
    
    setIsReplacing(true);
    try {
      // Send original image at FULL resolution
      const fullResCanvas = document.createElement("canvas");
      fullResCanvas.width = originalImage.width;
      fullResCanvas.height = originalImage.height;
      const fullResCtx = fullResCanvas.getContext("2d");
      fullResCtx.drawImage(originalImage, 0, 0);
      const imageDataBase64 = fullResCanvas.toDataURL("image/png").split(",")[1];
      
      if (originalImage) {
        setImageHistory(prev => {
          const newHistory = [...prev, originalImage];
          if (newHistory.length > MAX_HISTORY) {
            return newHistory.slice(-MAX_HISTORY);
          }
          return newHistory;
        });
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/editor/replace-text`, {
        image_data: imageDataBase64,
        original_text: selectedDetectedText.text,
        new_text: ""
      });
      
      if (response.data.success && response.data.image_data) {
        const editedImg = new Image();
        editedImg.crossOrigin = "anonymous";
        editedImg.onload = () => {
          setOriginalImage(editedImg);
          setShowDeleteDialog(false);
          setSelectedDetectedText(null);
          setDetectedTexts([]);
          setHasUnsavedChanges(true);
          toast.success(`Text "${selectedDetectedText.text}" wurde entfernt!`);
        };
        editedImg.onerror = () => {
          setImageHistory(prev => prev.slice(0, -1));
          toast.error("Fehler beim Laden des bearbeiteten Bildes");
        };
        editedImg.src = `data:image/png;base64,${response.data.image_data}`;
      } else {
        setImageHistory(prev => prev.slice(0, -1));
        toast.error(response.data.error || "KI konnte den Text nicht entfernen");
      }
    } catch (err) {
      console.error("Text deletion error:", err);
      setImageHistory(prev => prev.slice(0, -1));
      toast.error("Fehler beim Entfernen des Textes");
    } finally {
      setIsReplacing(false);
    }
  };

  // Undo
  const undoLastEdit = () => {
    if (imageHistory.length === 0) {
      toast.info("Keine vorherige Version verfügbar");
      return;
    }
    
    const previousImage = imageHistory[imageHistory.length - 1];
    setImageHistory(prev => prev.slice(0, -1));
    setOriginalImage(previousImage);
    setDetectedTexts([]);
    setHasUnsavedChanges(true);
    toast.success("Letzte Änderung rückgängig gemacht");
  };

  // Open dialogs
  const openReplaceDialog = (textItem) => {
    setSelectedDetectedText(textItem);
    setReplacementText(textItem.text);
    setShowReplaceDialog(true);
  };

  const openDeleteDialog = (textItem) => {
    setSelectedDetectedText(textItem);
    setShowDeleteDialog(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Calculate aspect ratio for canvas display
  const getAspectRatio = () => {
    if (currentFormat === "9:16") return "9/16";
    if (currentFormat === "1200x628") return "1200/628";
    return "1/1";
  };

  return (
    <div className="space-y-6 p-6" data-testid="meta-ad-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => {
            const fromJob = searchParams.get("fromJob");
            if (fromJob) {
              navigate(`/jobs?openJob=${fromJob}`);
            } else {
              navigate(-1);
            }
          }} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Meta Ad bearbeiten</h1>
            <p className="text-muted-foreground text-sm">
              {jobData?.title || "Meta Ad Editor"} • Format: {currentFormat}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {hasUnsavedChanges && (
            <Button 
              onClick={saveImageToDb}
              disabled={isSaving}
              className="gap-2 bg-green-600 hover:bg-green-700"
              data-testid="save-button"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Speichern
            </Button>
          )}
          {imageHistory.length > 0 && (
            <Button 
              variant="outline" 
              onClick={undoLastEdit}
              className="gap-2"
              data-testid="undo-button"
            >
              <Undo2 className="w-4 h-4" />
              Rückgängig ({imageHistory.length})
            </Button>
          )}
          <Button variant="outline" onClick={() => downloadImage("png")}>
            <Download className="w-4 h-4 mr-2" />
            PNG
          </Button>
          <Button onClick={() => downloadImage("jpg")}>
            <Download className="w-4 h-4 mr-2" />
            JPG
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Canvas */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-4">
              <div className="relative bg-muted/30 rounded-lg overflow-hidden flex justify-center">
                <canvas
                  ref={canvasRef}
                  className="max-w-full max-h-[600px] cursor-move"
                  style={{ aspectRatio: getAspectRatio() }}
                  onClick={handleCanvasClick}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Klicke auf Text zum Auswählen • Ziehe zum Verschieben
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          <Tabs defaultValue="ai">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="ai">
                <Wand2 className="w-4 h-4 mr-1" />
                KI
              </TabsTrigger>
              <TabsTrigger value="text">
                <Type className="w-4 h-4 mr-1" />
                Text
              </TabsTrigger>
              <TabsTrigger value="adjust">
                <Sun className="w-4 h-4 mr-1" />
                Bild
              </TabsTrigger>
            </TabsList>

            {/* AI Tab */}
            <TabsContent value="ai" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wand2 className="w-4 h-4" />
                    KI Text-Bearbeitung
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Lasse die KI den Text im Bild erkennen, ersetzen oder entfernen.
                  </p>
                  
                  <Button 
                    onClick={detectTextInImage} 
                    disabled={isDetecting}
                    className="w-full"
                    data-testid="detect-text-button"
                  >
                    {isDetecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Erkenne Text...
                      </>
                    ) : (
                      <>
                        <ScanText className="w-4 h-4 mr-2" />
                        Text erkennen
                      </>
                    )}
                  </Button>

                  {/* Detected Texts */}
                  {detectedTexts.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm">Erkannte Texte:</Label>
                      {detectedTexts.map((textItem, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-lg border bg-muted/30 space-y-2"
                          data-testid={`detected-text-${index}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{textItem.text}</p>
                            <p className="text-xs text-muted-foreground">
                              {textItem.position?.vertical} {textItem.position?.horizontal} • {textItem.size} • {textItem.color}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="flex-1"
                              onClick={() => openReplaceDialog(textItem)}
                            >
                              <Replace className="w-4 h-4 mr-1" />
                              Ersetzen
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="flex-1 text-destructive hover:text-destructive"
                              onClick={() => openDeleteDialog(textItem)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Löschen
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {detectedTexts.length === 0 && !isDetecting && (
                    <p className="text-xs text-center text-muted-foreground py-4">
                      Klicke auf "Text erkennen" um Texte im Bild zu finden
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Text Tab */}
            <TabsContent value="text" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Neuer Text</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="new-text">Text</Label>
                    <Input
                      id="new-text"
                      value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      placeholder="Text eingeben..."
                      data-testid="text-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Schriftart</Label>
                      <Select value={newTextFont} onValueChange={setNewTextFont}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONTS.map((font) => (
                            <SelectItem key={font.value} value={font.value}>
                              {font.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Farbe</Label>
                      <Select value={newTextColor} onValueChange={setNewTextColor}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TEXT_COLORS.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-4 h-4 rounded border"
                                  style={{ backgroundColor: color.value }}
                                />
                                {color.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Größe: {newTextSize}px</Label>
                    <Slider
                      value={[newTextSize]}
                      onValueChange={([v]) => setNewTextSize(v)}
                      min={24}
                      max={120}
                      step={2}
                    />
                  </div>
                  
                  <div>
                    <Label>Zeichenabstand: {newTextLetterSpacing}px</Label>
                    <Slider
                      value={[newTextLetterSpacing]}
                      onValueChange={([v]) => setNewTextLetterSpacing(v)}
                      min={-2}
                      max={20}
                      step={1}
                    />
                  </div>
                  
                  <Button onClick={addTextOverlay} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Text hinzufügen
                  </Button>
                </CardContent>
              </Card>

              {/* Text list */}
              {textOverlays.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Textelemente</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {textOverlays.map((overlay, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-colors ${
                          activeTextIndex === index ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                        }`}
                        onClick={() => setActiveTextIndex(index)}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: overlay.color }}
                          />
                          <span className="truncate text-sm">{overlay.text}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTextOverlay(index);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Adjust Tab */}
            <TabsContent value="adjust" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    Bildanpassungen
                    <Button variant="ghost" size="sm" onClick={resetAdjustments}>
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Sun className="w-4 h-4" />
                      Helligkeit: {brightness}%
                    </Label>
                    <Slider
                      value={[brightness]}
                      onValueChange={([v]) => setBrightness(v)}
                      min={50}
                      max={150}
                      step={1}
                    />
                  </div>
                  
                  <div>
                    <Label className="flex items-center gap-2">
                      <Contrast className="w-4 h-4" />
                      Kontrast: {contrast}%
                    </Label>
                    <Slider
                      value={[contrast]}
                      onValueChange={([v]) => setContrast(v)}
                      min={50}
                      max={150}
                      step={1}
                    />
                  </div>
                  
                  <div>
                    <Label className="flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Sättigung: {saturation}%
                    </Label>
                    <Slider
                      value={[saturation]}
                      onValueChange={([v]) => setSaturation(v)}
                      min={0}
                      max={200}
                      step={1}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Replace Text Dialog */}
      <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Text ersetzen</DialogTitle>
            <DialogDescription>
              Der erkannte Text wird durch die KI durch den neuen Text ersetzt.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Originaler Text</Label>
              <div className="p-2 bg-muted rounded-md mt-1">
                <p className="font-medium">{selectedDetectedText?.text}</p>
              </div>
            </div>
            
            <div>
              <Label htmlFor="replacement">Neuer Text</Label>
              <Input
                id="replacement"
                value={replacementText}
                onChange={(e) => setReplacementText(e.target.value)}
                placeholder="Neuen Text eingeben..."
                data-testid="replacement-text-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplaceDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={replaceTextInImage} 
              disabled={isReplacing || !replacementText.trim()}
              data-testid="confirm-replace-button"
            >
              {isReplacing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ersetze...
                </>
              ) : (
                <>
                  <Replace className="w-4 h-4 mr-2" />
                  Ersetzen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Text Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Text löschen</DialogTitle>
            <DialogDescription>
              Der erkannte Text wird durch die KI aus dem Bild entfernt.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Zu löschender Text</Label>
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md mt-1">
                <p className="font-medium text-destructive">{selectedDetectedText?.text}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Die KI wird versuchen, diesen Text zu entfernen und den Hintergrund passend zu rekonstruieren.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive"
              onClick={deleteTextInImage} 
              disabled={isReplacing}
              data-testid="confirm-delete-button"
            >
              {isReplacing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Lösche...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Text löschen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetaAdEditor;
